<template>
  <v-row class="ednDateTimePicker">
      <v-col
        :cols="timeDisabled ? '12':'' "
        v-if="!dateDisabled"
        class="field-date"
        :style="getDateInputWidth"
      >
        <v-menu
          ref="dateMenu"
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="auto"
          :zIndex="pickerZIndex"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              ref="dateTextField"
              class="dateField"
              v-model="dateFormatted"   
              persistent-placeholder
              :prepend-icon="getDatePrependIcon"
              v-bind="attrs"
              v-on="on"
              :rules="rules"
              :readonly="readonly"
              :filled="readonly"
              :disabled="disabled"
              :placeholder="placeholder ? placeholder : format"
              @blur="saveNewDateValue($event)"
              @keypress.enter.native="saveNewDateValue($event)"
              @keyup.delete="setDateTimeNull"
              :class="getValidationClass"
              :outlined="isValidated"
              :color="isValidated ? 'success':''"
              :dense="isValidated"
              :error-messages="errorMessages"
              :error="fldError"
            >
              <template v-slot:label >
                <span @click.prevent>{{ label }} </span>
                <span v-if="required && !readonly" class="red--text ml-1">*</span>
              </template>
              <template v-slot:append>
                <v-icon v-if="readonly" class="v-icon--disabled mt-2">mdi-lock</v-icon>
                <v-icon v-else-if="isValidated" color="green">mdi-check</v-icon>
              </template>
            </v-text-field>
          </template>
          <v-date-picker
            v-if="!readonly"
            v-model="datePicker"
            no-title
            :locale="locale"
            :color="primary"
            :allowed-dates="allowedDates"
            :first-day-of-week="1"
            @input="updateDatePickerValue"
          ></v-date-picker>
        </v-menu>
      </v-col>
      <v-col 
        v-if="!timeDisabled"
        class="field-time"
        :style="getTimeInputWidth"
      >
          <v-combobox 
              ref="timeField" 
              class="timeCombobox"
              menu-props="auto"
              :prepend-icon="getTimePrependIcon"
              v-model="timePicker"
              :items="timeItems"
              :readonly="readonly"
              :filled="readonly"
              :disabled="disabled"
              :rules="timeRules"
              @input="cleanTimeSyntax($event)"
              append-icon=""
              :class="getValidationClass"
              :outlined="isValidated"
              :color="isValidated ? 'success':''"
              :dense="isValidated"
              :error="errorState"
          >
            <template v-slot:label>
              <div v-if="dateDisabled">
                  <span>{{ label }} </span>
                  <span v-if="required && !readonly" class="red--text ml-1">*</span>
              </div>
            </template>
            <template v-slot:append>
                <v-icon v-if="readonly" class="v-icon--disabled mt-2">mdi-lock</v-icon>
                <v-icon v-else-if="isValidated" color="green">mdi-check</v-icon>
            </template>
          </v-combobox>
      </v-col> 
  </v-row>
</template>

<script> 
import { ednDate } from './mixins/ednDate'
import { ednRequired } from './mixins/ednRequired'

export default {
  data() {
      return {
        // date for picker
        datePicker: null,
        // display date for input
        dateFormatted: null,
        dateMenu: false,
        // list hours for dropdown
        timeList: [],
        timeItems: [],
        // store the hour 
        timePicker: null,
        primary: null,
        errorMessages:'' ,
        emptyDate:false   ,
        fldError:false,
        timeDeletion:false
      }
  },
  mounted() {
    this.primary = this.$vuetify.theme.currentTheme.primary;
  },
  props: {
    // required field 
    required: {
      type: Boolean,
      default: false
    },
    // readonly
    readonly: {
      type: Boolean,
      default: false
    },
    // label of the field
    label: {
      type: String,
      default: ''
    },
    // placeholder
    placeholder: {
      type: String,
      default: ''
    },
    // date value of parent
    dateValue: {
      type: String,
      default: null
    },
    // invalid date message
    invalidDateMsg: {
      type: String,
      default: () => '',
    },
    // minimum date : to disable the lower date
    minDate: {
      type: String,
      default: null
    },
    // date field's rules for validation
    customRules: {
      type: Array,
      default: () => []
    },
    // time field's rules for validation
    customTimeRules: {
      type: Array,
      default: () => []
    },
    // disabled field
    dateDisabled: {
      type: Boolean,
      default: false
    },
    timeDisabled: {
      type: Boolean,
      default: false
    }, 
    // if the date icons should be displayed or not
    dateIconHidden : {
      type: Boolean,
      default: false
    },
    // width of inputs
    inputsWidth: {
      type: Object,
      default() {
        return {  }
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isValidated:{
      type: Boolean,
      default: false
    },
    // type of validation ( blur, change ...)
    inputValidation:{
      type: String,
      default: 'blur'
    },
    /* Z-index of the date picker v-menu 
    to override other html element if necessary */
    pickerZIndex:{
      type: [String, Number],
      default: 8
    }
  },
  mixins: [ednDate, ednRequired],
  created() {
    // init the date values
    if((this.dateValue == null || this.dateValue == undefined || this.dateValue == "") && this.fldRequiredVal){
      this.errorMessages = this.requiredMsg;
    }
    this.updateDateValue(this.dateValue);

    //store on a data variable the hours list for the time field
    this.timeList = this.generateTimeList(30);
    this.timeItems = this.timeList;
  },
  computed: {
    timeRules () {
      let rules = [];
      if (this.customTimeRules.length) {
        rules = this.customTimeRules;
      } else {
        // defalut rules
        rules = [
          value => {
            const pattern = /^([01]\d|2[0-3]):[0-5]\d$/;
            const isValueValid = (value === null) || 
              (value === '') || 
              (typeof value === 'string' && pattern.test(value)) ||
              (typeof value === 'object' && value.text && pattern.test(value.text));

            return isValueValid || this.invalidDateMsg;
          },
        ];
      }

      return rules;
    },
    /** Icon for the time input */
    getTimePrependIcon(){
      if(!this.dateIconHidden) return 'mdi-clock-outline'
      return '';
    },
    /** Icon for the date input */
    getDatePrependIcon(){
      if(!this.dateIconHidden) return 'mdi-calendar'
      return '';
    },
    /** time input css width */
    getTimeInputWidth(){
      if(!!this.inputsWidth.time){
        return {
          width:`${this.inputsWidth.time}px`,
          maxWidth:`${this.inputsWidth.time}px`,
          flex:`1 1 ${this.inputsWidth.time}px`
        }
      }
      return {}
    },
    /** date input css width */
    getDateInputWidth(){
      if(!!this.inputsWidth.date){
        return {
          width:`${this.inputsWidth.date}px`,
          maxWidth:`${this.inputsWidth.date}px`,
          flex:`1 1 ${this.inputsWidth.date}px`
        }
      }
      return {}
    },
     /** Position of the validation icon */
     validIcon(){
      return !this.readonly ? 'append' : 'append';
    },
    /** Css class for field validation */
    getValidationClass(){
      return this.isValidated ? 'edn-validated green--text v-input--is-focused' : ''
    },
    /** The date is empty but not required */
    getEmptyDate(){
      return this.emptyDate && !this.fldRequiredVal;
    },
    /** The date is empty and required */
    requiredEmptyDate(){
      return this.emptyDate && this.fldRequiredVal;
    },
    /** Put an error state if there is an error message */
    errorState(){
      return this.errorMessages != '';
    }
  },
  watch: {
    datePicker (val, oldVal) {
      if (val != oldVal) {
        // update the value of field when the date picker modified
        this.dateFormatted = this.formatDate(val);

        //check minDate exist or not, if exist recalculate the hour list
        if(this.minDate) {
          this.timeItems = this.getAllowedHours();
        }
        this.emptyDate = !val
        if(!this.requiredEmptyDate)  this.sendValuetoParent();
        
        this.timeDeletion = true;
      }
    },
    timePicker (val, oldVal) {
      // the value of the field time is changed, send event
        if(val != oldVal && !this.requiredEmptyDate && !this.timeDeletion)  this.sendValuetoParent();
        this.timeDeletion = false;
    },
    //create a new watch event to watch the minDate, check the datePicker exist or not, if exist recalculate the hour list
    minDate(val) {
      if(this.datePicker && val) {
        this.timeItems = this.getAllowedHours();
      }
    },
    dateValue(val) {
      // modify the value for input and date picker
      this.updateDateValue(val);
    }
  },
  methods: {
    /**
    * format date from date picker, which the format will always be yyyy-MM-dd
    */
    formatDate (date) {
      if (!date) return null;

      const parsed = this.fnsParse(date, 'yyyy-MM-dd', new Date(), { locale:this.getDateFnsLocale() });
      if (this.fnsIsValid(parsed)) {
        // reset the validation if the date value update correct 
        this.$refs['dateTextField'].resetValidation();

        // set time field defalut value : 00:00 as default
        if (!this.timeDisabled && !this.timePicker) {
          this.timePicker = "00:00"
        }

        return this.fnsFormat(parsed, this.format, { locale: this.getDateFnsLocale() });
      }

      return null;
    },
    /**
    * format date from input, and sync to date picker 
    */
    parseDate (date) {
      if (!date) return null;

      const parsed = this.fnsParse(date, this.format, new Date(), { locale: this.getDateFnsLocale() });
      if (this.fnsIsValid(parsed)) {
        return this.fnsFormat(parsed, 'yyyy-MM-dd');
      }
      // for the moment, it will return null, this part will change in next US
      return date;
    },
    // save the new date Value
    saveNewDateValue (e) {
      if (e && e.target){
        if (e.target.value != this.dateFormatted) {
          this.dateFormatted = e.target.value;
        }
        const parsed = this.fnsParse(this.dateFormatted, this.format, new Date(), { locale: this.getDateFnsLocale() });

        if (this.fnsIsValid(parsed) || !this.dateFormatted) {
          // check minDate 
          if (this.minDate) {
            let minDateObj = new Date(this.minDate);
            if (this.fnsIsValid(minDateObj) && parsed < minDateObj) {
              this.dateFormatted = this.fnsFormat(minDateObj, this.format, { locale: this.getDateFnsLocale() });
            }      
          }
            if(this.dateFormatted === "") this.emptyDate = true;
            this.datePicker = this.parseDate(this.dateFormatted);
          // the value of the field date is changed, send event
          if(!this.requiredEmptyDate) this.sendValuetoParent();
        }
      // if the value return in param is not the event (blur) we check if it's a date value and then covnert it to save it
       }
       else if(!e.target && this.fnsIsValid(new Date(e))){
         this.datePicker = this.parseDate(e);
      }
    },
    /**
    * modify the value for input and date picker, there is a watch function for datePicker to update dateFormatted
    */
    updateDateValue (val) {
      let newDateValue = this.fnsParseISO(val);
      if (this.fnsIsValid(newDateValue)) {
        let newDate = this.fnsFormat(newDateValue, 'yyyy-MM-dd');
        let newTime = this.fnsFormat(newDateValue, "HH:mm");

        if (newDate != this.datePicker) {
          this.datePicker = newDate;
        }

        if (this.timePicker != newTime) {
          this.timePicker = newTime;
        }
      }else if(val == null){
        this.setDateTimeNull();
      }
    },
    /**
    * rewrite the rules from ednRequired
    */
    isRequired() {
      let dateRules = [];
      if (this.fldRequiredVal) {
        dateRules.push((value) => {
          return (value && !!value) || this.requiredMsg;
        });
      }

      let that = this;
      dateRules.push(function (v) {
        // Règle poussée pour faire un callback en cas d'erreur de format.
        if(!that.fldRequiredVal && (v === "" ||  v === null || v === undefined)) {
          return true;
        } else {
          return (
            that.fnsIsValid(that.fnsParse(v, that.format, new Date(), { locale: that.getDateFnsLocale() })) || that.invalidDateMsg
          )
        }
      });

      // insert custom rlues
      if (this.customRules.length) {
        dateRules.concat(this.customRules);
      }

      return dateRules;
    },
    /**
    * disable the lower date
    */
    allowedDates (val) {
      let minDateValue = this.fnsParseISO(this.minDate);
      if (this.fnsIsValid(minDateValue)) {
        return val >= this.fnsFormat(new Date(this.minDate), 'yyyy-MM-dd');
      }
      else {
        return true;
      }
    },
    /**
     * generate the hours list for time field
     */
    generateTimeList(minutesInterval) {
      let startTime = 0;
      let times = [];

      // Loop to generate time slots in the specified interval until the end of the day (23:30)
      while (startTime < 24 * 60) {
        // Get the hours of the day in 24-hour format
        let hourFormat = Math.floor(startTime / 60);
        // Get the minutes of the hour
        let minuteFormat = startTime % 60;
        // Format the time string in [00:00 - 24:00] format using padStart to add leading zeros
        let timeText = `${String(hourFormat).padStart(2, '0')}:${String(minuteFormat).padStart(2, '0')}`;

        times.push({
          text: timeText,
          disabled: false
        });
        // Move to the next time slot by incrementing startTime with the specified interval
        startTime += minutesInterval;
      }

      return times;
    },
    /**
     * create a function to recalculate hour list
     */
    getAllowedHours() {
      let hours = JSON.parse(JSON.stringify(this.timeList));
      let minDateValue = this.fnsParseISO(this.minDate);
      if (this.fnsIsValid(minDateValue)) {
        let value = this.fnsFormat(minDateValue, 'yyyy-MM-dd');
        if(value === this.datePicker){
          for(var item = 0; item < hours.length; item++) {
            //extract the hour from object item 
            let hour = (hours[item].text).split(':')[0]; 
              // extract the minutes from object item
            let minute = (hours[item].text).split(':')[1];
            if(hour < this.getHours(minDateValue)) { 
              //compare if the current hour on the list is smaller than the hour from minDate, if it's true disable all the hours until minDate hour
              hours[item].disabled = true;
            } else {
              if(hour == this.getHours(minDateValue)) { 
                //compare if the current hour on the list is the same as the hour from minDate, if it's true we need to compare the minutes
                if(minute <= this.getMinutes(minDateValue)){ 
                  //compare if the current minutes are smaller or equal to minutes from minDate, then disabled all the hours until the minDate time
                  hours[item].disabled = true;
                }
              }
            }
          }
        }
      } 
      return hours;
    },
    sendValuetoParent() {
      // check the date and time value is modified or not
      let newDateValue = null;
      let newTimeValue = null;
      if (this.timePicker && this.timePicker.text) {
        newDateValue = this.fnsParseISO(this.datePicker + ' ' + this.timePicker.text);
        newTimeValue = this.timePicker.text;
      } else if (this.timePicker && typeof this.timePicker === 'string' && this.timePicker) {
        newDateValue = this.fnsParseISO(this.datePicker + ' ' + this.timePicker);
        newTimeValue = this.timePicker;
      } else if(!this.getEmptyDate) {
        newDateValue = this.fnsParseISO(this.datePicker);
      }

      if ((this.fnsIsValid(newDateValue) || this.getEmptyDate)) {
        this.errorMessages = '';
        this.fldError = false;
        let dateValue = !this.getEmptyDate ? newDateValue.toISOString() : newTimeValue;
        if (dateValue != this.dateValue) {
          this.$emit('newDateValue', {
            date: this.datePicker,
            time: newTimeValue,
            dateValue: dateValue,
            emptyDate:this.getEmptyDate
          });
        }
      } else {
        this.$emit('newDateValue', {
          date: this.datePicker,
          time: newTimeValue,
          dateValue: null,
          emptyDate:this.getEmptyDate
        });
        this.fldError = true;
      }
    },
    /* remove special characters */
    cleanTimeSyntax (time) {
      if (time != null && !(typeof time === 'object' && time.text)) {
        const regex = /[^0-9:]|(?<=:)(?=\d)/g;
        this.timePicker = time.replace(regex, '');
        this.$refs['timeField'].lazyValue = this.timePicker;
        this.$refs['timeField'].lazySearch = this.timePicker;
      }
    },
    /* set the time field on null if the date is deleted(empty) */
    setDateTimeNull() {
      this.dateFormatted = null;
      this.datePicker = null;
      this.timePicker = null;
    },
    /** Update the date picker value when clicking on the datepicker modal */
    updateDatePickerValue(e){
      this.dateMenu = false;
      this.datePicker = e;
    }
  }
}
</script>

<style scoped>

</style>
